import React, { useContext } from "react";
import {
  AppBar,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Button,
  Container,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import { styled, useTheme } from "@mui/material/styles";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import Bunny from "../assets/ratingbunny_core.jpg";
import AuthContext from "../context/AuthContext";

// Styling using MUI's styled utility
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main, // Set to primary color or desired background color
  color: theme.palette.text.link, // Set to primary color or desired background color
  top: 0, // Aligns it to the top
  width: "100%",
  zIndex: theme.zIndex.appBar, // Ensures it stays above content
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: "32px", // Reduce or adjust this value as needed
  padding: theme.spacing(0), // Adjust the padding, possibly reduce or remove
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  [theme.breakpoints.up("md")]: {
    minHeight: "32px",
  },
}));

const CenterBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.link,
  textDecoration: "none",
  fontFamily: theme.typography.fontFamily,
  fontWeight: 700,
  letterSpacing: ".3rem",
  textAlign: "center",
}));

const CustomNav = ({ showSearch, setShowSearch }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isOnSearchPage = location.pathname === "/";
  const isOnProPage = location.pathname.startsWith("/pro");
  const { user, logout } = useContext(AuthContext);

  const handleSearchClick = () => {
    setShowSearch((prev) => !prev); // Simply toggle the filter visibility
  };

  const displayName = (() => {
    if (!user) return "";
    const raw =
      user.displayName ||
      user.name ||
      (user.email ? user.email.split("@")[0] : "");
    if (!raw) return "";
    return raw.length > 8 ? raw.slice(0, 8) : raw;
  })();

  const isProUser = user?.plan?.startsWith("pro");
  const mobileActions = [
    {
      key: "search",
      label: "Search",
      icon: <SearchIcon />,
      onClick: () => {
        if (!isOnSearchPage) {
          navigate("/");
          return;
        }
        handleSearchClick();
      },
    },
    {
      key: "contact",
      label: "Contact",
      icon: <MailOutlineIcon />,
      onClick: () => navigate("/contact"),
    },
  ];

  if (user) {
    mobileActions.push(
      {
        key: "account",
        label: displayName || "Account",
        icon: <PersonIcon />,
        onClick: () => navigate("/pro"),
      },
      {
        key: "logout",
        label: "Logout",
        icon: <LogoutIcon />,
        onClick: logout,
      }
    );
  } else {
    mobileActions.push(
      {
        key: "signup",
        label: "Sign Up",
        icon: <PersonAddIcon />,
        onClick: () => navigate("/signup"),
      },
      {
        key: "login",
        label: "Login",
        icon: <LoginIcon />,
        onClick: () => navigate("/login"),
      }
    );
  }

  return (
    <>
      <StyledAppBar position="fixed">
        <Container maxWidth="xl">
          <StyledToolbar disableGutters>
            {!isSmDown && (
              <Tooltip title="Search">
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSearchClick}
                    startIcon={<SearchIcon />}
                    disabled={!isOnSearchPage}
                    aria-label="Search"
                  />
                </span>
              </Tooltip>
            )}
            <CenterBox sx={{ flexGrow: 1 }}>
              <Tooltip title="Home">
                <StyledTitle variant="h6" noWrap component={RouterLink} to={"/"}>
                  Rating
                </StyledTitle>
              </Tooltip>

              <Tooltip title="About">
                <Box
                  component={RouterLink}
                  to="/about"
                  sx={{
                    display: "inline-block",
                    margin: "0 8px",
                    textDecoration: "none", // Remove underline from link
                  }}
                >
                  <Box
                    component="img"
                    sx={{
                      maxHeight: { xs: 45, md: 50 },
                      maxWidth: { xs: 45, md: 50 },
                      margin: "0 8px",
                    }}
                    alt="Bunny logo"
                    src={Bunny} // Replace with actual Logo variable
                  />
                </Box>
              </Tooltip>

              <Tooltip title="Home">
                <StyledTitle variant="h6" noWrap component={RouterLink} to={"/"}>
                  Bunny
                </StyledTitle>
              </Tooltip>
            </CenterBox>

            {!isSmDown && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Contact">
                  <Button
                    component={RouterLink}
                    to={"/contact"}
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<MailOutlineIcon />}
                    aria-label="Contact"
                  />
                </Tooltip>
                {user ? (
                  <>
                    {displayName && (
                      isProUser ? (
                        <Typography
                          variant="body2"
                          sx={{ alignSelf: "center", textDecoration: "none", color: "inherit" }}
                          component={RouterLink}
                          to="/pro"
                        >
                          {displayName}
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ alignSelf: "center" }}>
                          {displayName}
                        </Typography>
                      )
                    )}
                    <Tooltip title="Logout">
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={logout}
                        startIcon={<LogoutIcon />}
                        aria-label="Logout"
                      />
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Tooltip title="Sign Up">
                      <Button
                        component={RouterLink}
                        to="/signup"
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<PersonAddIcon />}
                        aria-label="Sign Up"
                      />
                    </Tooltip>
                    <Tooltip title="Login">
                      <Button
                        component={RouterLink}
                        to="/login"
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<LoginIcon />}
                        aria-label="Login"
                      />
                    </Tooltip>
                  </>
                )}
              </Box>
            )}
          </StyledToolbar>
        </Container>
      </StyledAppBar>
      {!isOnProPage && isSmDown && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <BottomNavigation
            showLabels
            sx={{ backgroundColor: theme.palette.background.paper }}
          >
            {mobileActions.map((action) => (
              <BottomNavigationAction
                key={action.key}
                label={action.label}
                icon={action.icon}
                onClick={action.onClick}
              />
            ))}
          </BottomNavigation>
        </Box>
      )}
    </>
  );
};

export default CustomNav;
